/**
 * Handwriting recognizer client. The actual TrOCR inference runs in a Web
 * Worker (see handwriting.worker.ts): the worker owns its own WebAssembly
 * heap — separate from the DrawingWeb CAD engine heap, which starved the
 * model on memory-constrained tablets — and keeps the UI thread responsive
 * during inference.
 *
 * Model files are served by the app itself, from /hw-models/ (placed there
 * at build time by script/fetch-handwriting-model.mjs). They are fetched on
 * first use and kept in the browser Cache Storage by transformers.js, so
 * later uses work offline. The worker bundle and ONNX Runtime WASM are
 * runtime-cached by the service worker (see vite.config.ts).
 *
 * Device notes (see POC-DRAW2TEXT + integration debugging):
 * - webgpu inference conflicts with the DrawingWeb WebGL context and is
 *   flaky on mobile GPUs -> wasm by default, webgpu opt-in via the
 *   DR_HANDWRITING_DEVICE env for experimentation.
 */

import { Logger } from "@/logger";
import type { HandwritingRecognizer } from "@/drawing/handwriting/types";

/** Model folder under the base path. Override with DR_HANDWRITING_MODEL. */
const DEFAULT_MODEL_ID = "trocr-small-fr-v1";

/** Where the model is served from — must stay a relative path (see worker). */
const DEFAULT_MODEL_BASE = "/hw-models/";

/** Upper bound for one request, first model download included. */
const REQUEST_TIMEOUT_MS = 240_000;

/**
 * ONNX Runtime Web ships only a SIMD build, so a browser without WebAssembly
 * SIMD cannot run recognition at all (older Chromium forks — some tablet
 * vendors ship those). Detect it up front to fail with a clear message
 * instead of an opaque backend error.
 * Bytes: a minimal module using the v128 type.
 */
function hasWasmSimd(): boolean {
  try {
    return WebAssembly.validate(
      new Uint8Array([
        0, 97, 115, 109, 1, 0, 0, 0, 1, 5, 1, 96, 0, 1, 123, 3, 2, 1, 0, 10,
        10, 1, 8, 0, 65, 0, 253, 15, 253, 98, 11,
      ])
    );
  } catch {
    return false;
  }
}

export interface DownloadProgress {
  file?: string;
  loaded?: number;
  total?: number;
}

interface WorkerResponse {
  id: number;
  ok?: boolean;
  text?: string;
  error?: string;
  progress?: DownloadProgress;
}

interface PendingRequest {
  resolve: (text: string) => void;
  reject: (err: Error) => void;
  timer: ReturnType<typeof setTimeout>;
  onProgress?: (progress: DownloadProgress) => void;
}

export class TrocrRecognizer implements HandwritingRecognizer {
  private worker: Worker | null = null;
  private nextRequestId = 0;
  private pending = new Map<number, PendingRequest>();

  private readonly config = {
    modelId:
      (import.meta.env.DR_HANDWRITING_MODEL as string | undefined) ||
      DEFAULT_MODEL_ID,
    modelBase:
      (import.meta.env.DR_HANDWRITING_MODEL_BASE as string | undefined) ||
      DEFAULT_MODEL_BASE,
    device:
      (import.meta.env.DR_HANDWRITING_DEVICE as string | undefined) === "webgpu"
        ? ("webgpu" as const)
        : ("wasm" as const),
    dtype:
      (import.meta.env.DR_HANDWRITING_DTYPE as string | undefined) || "q8",
  };

  warmup(): void {
    this.request("warmup").catch((err) => {
      Logger.error(`TrocrRecognizer: warmup failed: ${err}`);
    });
  }

  /**
   * Download and initialize the model, reporting download progress.
   * Used by the onboarding modal; resolves when the model is ready.
   */
  async installModel(
    onProgress?: (progress: DownloadProgress) => void
  ): Promise<void> {
    await this.request("warmup", undefined, onProgress);
  }

  async recognize(imageDataUrl: string): Promise<string> {
    return this.request("recognize", imageDataUrl);
  }

  private request(
    type: "warmup" | "recognize",
    image?: string,
    onProgress?: (progress: DownloadProgress) => void
  ): Promise<string> {
    if (!hasWasmSimd()) {
      return Promise.reject(
        new Error("navigateur incompatible : WebAssembly SIMD non supporté")
      );
    }

    const worker = this.getWorker();
    const id = this.nextRequestId++;

    return new Promise<string>((resolve, reject) => {
      const timer = setTimeout(() => {
        this.pending.delete(id);
        reject(new Error("délai de reconnaissance dépassé"));
      }, REQUEST_TIMEOUT_MS);
      this.pending.set(id, { resolve, reject, timer, onProgress });
      worker.postMessage({ id, type, config: this.config, image });
    });
  }

  private getWorker(): Worker {
    if (!this.worker) {
      Logger.info(
        `TrocrRecognizer: starting worker for ${this.config.modelId} ` +
          `(${this.config.device}/${this.config.dtype})`
      );
      this.worker = new Worker(
        new URL("./handwriting.worker.ts", import.meta.url),
        { type: "module" }
      );
      this.worker.onmessage = (event: MessageEvent<WorkerResponse>) => {
        const { id, ok, text, error, progress } = event.data;
        const entry = this.pending.get(id);
        if (!entry) return;
        if (progress) {
          entry.onProgress?.(progress);
          return;
        }
        this.pending.delete(id);
        clearTimeout(entry.timer);
        if (ok) {
          entry.resolve(text ?? "");
        } else {
          entry.reject(new Error(error ?? "erreur inconnue du worker"));
        }
      };
      this.worker.onerror = (event) => {
        // Worker-level failure (script load, crash): fail everything in
        // flight and drop the worker so the next attempt restarts fresh.
        Logger.error(`TrocrRecognizer: worker error: ${event.message}`);
        const err = new Error(event.message || "le worker a échoué");
        for (const [id, entry] of this.pending) {
          this.pending.delete(id);
          clearTimeout(entry.timer);
          entry.reject(err);
        }
        this.worker?.terminate();
        this.worker = null;
      };
    }
    return this.worker;
  }
}

/**
 * Quick eligibility check, without downloading anything: catches the cases
 * that make recognition impossible up front (no Worker, no WASM SIMD —
 * older Chromium forks shipped by some tablet vendors). Memory exhaustion
 * can only be detected by actually loading the model (installModel).
 */
export function checkHandwritingSupport(): {
  supported: boolean;
  reason?: string;
} {
  if (typeof Worker === "undefined") {
    return { supported: false, reason: "Web Workers non supportés" };
  }
  if (typeof WebAssembly === "undefined") {
    return { supported: false, reason: "WebAssembly non supporté" };
  }
  if (!hasWasmSimd()) {
    return { supported: false, reason: "WebAssembly SIMD non supporté" };
  }
  return { supported: true };
}

let sharedRecognizer: TrocrRecognizer | null = null;

/** App-wide recognizer instance (the model is heavy — load it once). */
export function getHandwritingRecognizer(): TrocrRecognizer {
  if (!sharedRecognizer) {
    sharedRecognizer = new TrocrRecognizer();
  }
  return sharedRecognizer;
}
